import React, {useEffect, useState, useRef} from 'react'

import {
    Box,
    Spinner,

    /* Hooks */
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'

/* Styles */
import {MyRewardsStylesProvider} from './styles/myRewardsStyles'

/* Hooks */
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useOptimizely} from '../../contexts/optimizely-context'
import {useLoyaltyData} from '../../contexts/loyalty-data-context'

/* Providers */
import {OptimizelyProvider} from '../../contexts/optimizely-context'
import {LoyaltyDataProvider} from '../../contexts/loyalty-data-context'

/* Components */
import ExclusiveRewardsBlock from './partials/exclusive-rewards-block'
import ExclusiveRewardsBlockCMS from './partials/exclusive-rewards-block-cms'
import {useIntl} from 'react-intl'
import Seo from '../../components/seo'
import {renderPagesSwitch} from '../../components/optimizely/utils/contentRenderer'

const MyRewardsContent = () => {
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const {rewardPageBlock, optimizelyData, isOptimizelyLoading} = useOptimizely()
    const cmsPage = optimizelyData?.item
    const {loyaltyData, isLoading: isLoyaltyDataLoading} = useLoyaltyData()
    const {data: customer} = useCurrentCustomer()
    const exclusiveRewardsRef = useRef(null)
    const [mounted, setMounted] = useState(false)
    const sflmEnabled = getSiteEnvVarValue(appConfig, 'SFLM_ENABLED', siteId)
    const {formatMessage} = useIntl()

    const rewardsPageTitle = formatMessage({
        id: 'rewards.page.title',
        defaultMessage: 'My Rewards | Garage Clothing UK'
    })

    const rewardsPageDescription = formatMessage({
        id: 'rewards.page.description',
        defaultMessage: 'Track your Rewards and Loyalty Program at Garage Clothing UK.'
    })

    useEffect(() => {
        setMounted(true)
    }, [])

    const seo = (
        <Seo
            title={rewardsPageTitle}
            description={rewardsPageDescription}
            openGraphTags={[
                {
                    property: 'og:title',
                    content: rewardsPageTitle
                },
                {
                    property: 'og:description',
                    content: rewardsPageDescription
                }
            ]}
        />
    )

    if (!mounted) {
        return seo
    }

    if (isOptimizelyLoading && isLoyaltyDataLoading) {
        return (
            <>
                {seo}
                <Box data-testid="error-page">
                    <Spinner />
                </Box>
            </>
        )
    }

    return (
        <>
            {seo}
            {isHydrated() && (
                <Box data-testid="sf-my-rewards-container">
                    {/* CMS-driven content (MainRegion blocks rendered via renderPagesSwitch) */}
                    {cmsPage && renderPagesSwitch(cmsPage)}

                    {sflmEnabled && customer.isRegistered && (
                        <>
                            {/* Exclusive rewards block - only choose SFCC vs CMS after loyalty data has loaded to avoid CMS flashing then switching to SFCC */}
                            {!isLoyaltyDataLoading &&
                                (loyaltyData?.rewardsDataMapping ? (
                                    /* SFCC Content */
                                    <ExclusiveRewardsBlock
                                        ref={exclusiveRewardsRef}
                                        loyaltyData={loyaltyData}
                                        isLoyaltyDataLoading={isLoyaltyDataLoading}
                                    />
                                ) : (
                                    /* CMS Content only if we have configured */
                                    rewardPageBlock && (
                                        <ExclusiveRewardsBlockCMS
                                            ref={exclusiveRewardsRef}
                                            loyaltyData={loyaltyData}
                                            isLoyaltyDataLoading={isLoyaltyDataLoading}
                                        />
                                    )
                                ))}
                        </>
                    )}
                </Box>
            )}
        </>
    )
}

const MyRewards = () => {
    const styles = useMultiStyleConfig('MyRewards')

    return (
        <OptimizelyProvider pageIdentifier="grg-web-rewards-page">
            <LoyaltyDataProvider>
                <MyRewardsStylesProvider value={styles}>
                    <MyRewardsContent />
                </MyRewardsStylesProvider>
            </LoyaltyDataProvider>
        </OptimizelyProvider>
    )
}

MyRewards.getTemplateName = () => 'my-rewards'

export default MyRewards
